import { isChunkLoadError } from "@/components/error-boundary";
import type { TDesignSystem } from "@/shared/types";
import { useAppDispatch } from "@store";
import { openDialog } from "@store/dialogs";
import { useRef, useState } from "react";
import { toast } from "react-toastify";

type DesignSystemDialogModule = typeof import(
  "@/pages/editor/modules/design-system/components/design-system-dialog"
);

/**
 * Lazy-loads the editor's design-system dialog and opens it from anywhere
 * on the account page (default-theme-card edit button, picker popover edit
 * action, etc.). Caches the dynamic import so subsequent opens skip the
 * network round-trip.
 */
export const useOpenDesignSystemDialog = () => {
  const dispatch = useAppDispatch();
  const [isOpening, setIsOpening] = useState(false);

  const promiseRef = useRef<Promise<DesignSystemDialogModule> | null>(null);
  const moduleRef = useRef<DesignSystemDialogModule | null>(null);

  const loadModule = async (): Promise<DesignSystemDialogModule> => {
    if (moduleRef.current) return moduleRef.current;
    if (!promiseRef.current) {
      promiseRef.current = import(
        "@/pages/editor/modules/design-system/components/design-system-dialog"
      )
        .then((mod) => {
          moduleRef.current = mod;
          return mod;
        })
        .catch((error) => {
          // Clear the cached promise so the next click retries.
          promiseRef.current = null;
          throw error;
        });
    }
    return promiseRef.current;
  };

  const open = async (
    theme: Pick<
      TDesignSystem,
      "_id" | "designSystem" | "title" | "customColors"
    >,
  ) => {
    if (isOpening) return;
    setIsOpening(true);
    try {
      const mod = await loadModule();
      const DesignSystemDialog = mod.default;
      dispatch(
        openDialog({
          id: DesignSystemDialog.id,
          content: DesignSystemDialog,
          props: {
            themeRecord: {
              _id: theme._id,
              designSystem: (theme.designSystem ?? {}) as Record<
                string,
                unknown
              >,
              title: theme.title,
              customColors: theme.customColors,
            },
          },
        }),
      );
    } catch (error) {
      console.error(error);
      toast.error(
        isChunkLoadError(error)
          ? "Couldn't load the design system. Please refresh the page and try again."
          : "Couldn't open the design system. Please try again.",
      );
    } finally {
      setIsOpening(false);
    }
  };

  return { open, isOpening };
};
